import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PeriodPicker } from "@/components/PeriodPicker";
import { Select } from "@/components/ui/select";
import { DailyTrendChart } from "@/components/charts/DailyTrendChart";
import { HourlyHeatmap } from "@/components/charts/HourlyHeatmap";
import { TopUsersChart } from "@/components/charts/TopUsersChart";
import { FeedbackChart } from "@/components/charts/FeedbackChart";
import { presetRange, type DateRange, type Preset } from "@/lib/periods";
import { api } from "@/lib/api";
import { fmt, fmtMs, fmtModel } from "@/lib/utils";
import { searchModeLabel } from "@/lib/types";
import type {
  DailyPoint,
  FeedbackResponse,
  HourlyCell,
  ModelUsageResponse,
  SearchModeResponse,
  SummaryResponse,
  TopNegativeItem,
  TopUserItem,
} from "@/lib/types";

export function StatsPage() {
  const [preset, setPreset] = useState<Preset>("last30");
  const [custom, setCustom] = useState<DateRange>(presetRange("last30"));
  const range = preset === "custom" ? custom : presetRange(preset);
  const [channel, setChannel] = useState<"any" | "nw" | "web">("any");
  const params = { from: range.from, to: range.to };
  // 채널 인지 집계용 파라미터(피드백 집계는 채널 비적용이라 params 그대로 사용).
  const statParams = channel === "any" ? params : { ...params, channel };

  const summary = useQuery({
    queryKey: ["summary", range, channel],
    queryFn: () => api.get<SummaryResponse>("/stats/summary", statParams),
  });
  const daily = useQuery({
    queryKey: ["daily", range, channel],
    queryFn: () =>
      api
        .get<{ items: DailyPoint[] }>("/stats/daily", statParams)
        .then((r) => r.items),
  });
  const hourly = useQuery({
    queryKey: ["hourly", range, channel],
    queryFn: () =>
      api
        .get<{ items: HourlyCell[] }>("/stats/hourly", statParams)
        .then((r) => r.items),
  });
  const topUsers = useQuery({
    queryKey: ["top-users", range, channel],
    queryFn: () =>
      api
        .get<{ items: TopUserItem[] }>("/stats/top-users", { ...statParams, limit: 10 })
        .then((r) => r.items),
  });
  const models = useQuery({
    queryKey: ["model-usage", range, channel],
    queryFn: () => api.get<ModelUsageResponse>("/stats/models", statParams),
  });
  const searchModes = useQuery({
    queryKey: ["search-mode-usage", range, channel],
    queryFn: () => api.get<SearchModeResponse>("/stats/search-modes", statParams),
  });
  const feedback = useQuery({
    queryKey: ["feedback-daily", range],
    queryFn: () => api.get<FeedbackResponse>("/stats/feedback", params),
  });
  const negTop = useQuery({
    queryKey: ["top-negative", range],
    queryFn: () =>
      api
        .get<{ items: TopNegativeItem[] }>("/stats/feedback/top-negative", {
          ...params,
          limit: 10,
        })
        .then((r) => r.items),
  });

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">통계 / 보고서</h1>
        <p className="text-xs text-muted-foreground">
          기간 프리셋 · 시각화 · 미답변 / 피드백 분석
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <PeriodPicker
            preset={preset}
            custom={custom}
            onChange={(p, c) => {
              setPreset(p);
              setCustom(c);
            }}
          />
          <label className="block text-xs">
            <span className="mb-1 block text-muted-foreground">채널</span>
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value as "any" | "nw" | "web")}
            >
              <option value="any">전체</option>
              <option value="nw">네이버웍스</option>
              <option value="web">웹</option>
            </Select>
          </label>
        </CardContent>
      </Card>

      {/* 요약 KPI */}
      <Card>
        <CardHeader>
          <CardTitle>요약 KPI</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4 lg:grid-cols-6">
          {summary.isLoading || !summary.data ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))
          ) : (
            <>
              <Stat label="총 질의" value={fmt(summary.data.total)} />
              <Stat
                label="답변율"
                value={
                  summary.data.answer_rate !== null
                    ? `${(summary.data.answer_rate * 100).toFixed(1)}%`
                    : "-"
                }
              />
              <Stat label="평균 응답" value={fmtMs(summary.data.avg_response_ms)} />
              <Stat label="활성 사용자" value={fmt(summary.data.active_users)} />
              <Stat label="미답변" value={fmt(summary.data.unanswered)} />
              <Stat
                label="👍 / 👎"
                value={`${fmt(summary.data.pos_feedback)} / ${fmt(summary.data.neg_feedback)}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* 일별 추이 + 히트맵 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>일별 추이</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.isLoading ? (
              <Skeleton className="h-[280px]" />
            ) : (
              <DailyTrendChart data={daily.data ?? []} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>요일×시간 분포</CardTitle>
          </CardHeader>
          <CardContent>
            {hourly.isLoading ? (
              <Skeleton className="h-[280px]" />
            ) : (
              <HourlyHeatmap data={hourly.data ?? []} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* 사용자 TOP */}
      <Card>
        <CardHeader>
          <CardTitle>사용자 TOP 10</CardTitle>
        </CardHeader>
        <CardContent>
          {topUsers.isLoading ? (
            <Skeleton className="h-[260px]" />
          ) : (
            <TopUsersChart data={topUsers.data ?? []} />
          )}
        </CardContent>
      </Card>

      {/* 모델·검색 방법별 사용 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>모델(LLM)별 사용</CardTitle>
          </CardHeader>
          <CardContent>
            {models.isLoading ? (
              <Skeleton className="h-[160px]" />
            ) : (
              <UsageBarList
                grand={models.data?.total ?? 0}
                rows={(models.data?.items ?? []).map((it) => ({
                  key: it.model,
                  label: it.model === "(미기록)" ? "(미기록)" : fmtModel(it.model),
                  title:
                    it.model === "(미기록)"
                      ? "모델 기록 도입 이전 로그·스킬 산출 등"
                      : it.model,
                  total: it.total,
                  avg_ms: it.avg_ms,
                  muted: it.model === "(미기록)",
                }))}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>검색 방법별 사용</CardTitle>
          </CardHeader>
          <CardContent>
            {searchModes.isLoading ? (
              <Skeleton className="h-[160px]" />
            ) : (
              <UsageBarList
                grand={searchModes.data?.total ?? 0}
                rows={(searchModes.data?.items ?? []).map((it) => ({
                  key: it.mode,
                  label: it.mode === "(미기록)" ? "(미기록)" : searchModeLabel(it.mode),
                  title: it.mode,
                  total: it.total,
                  avg_ms: it.avg_ms,
                  muted: it.mode === "(미기록)",
                }))}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* 피드백 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>일별 피드백 분포</CardTitle>
          </CardHeader>
          <CardContent>
            {feedback.isLoading ? (
              <Skeleton className="h-[260px]" />
            ) : (
              <FeedbackChart data={feedback.data?.items ?? []} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>👎 받은 질문 TOP 10</CardTitle>
          </CardHeader>
          <CardContent>
            {negTop.isLoading ? (
              <Skeleton className="h-[260px]" />
            ) : (
              <NegativeList items={negTop.data ?? []} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

interface UsageBarRow {
  key: string;
  label: string;
  title?: string;
  total: number;
  avg_ms: number | null;
  muted?: boolean;
}

function UsageBarList({ rows, grand }: { rows: UsageBarRow[]; grand: number }) {
  if (rows.length === 0 || grand === 0) {
    return <div className="text-sm text-muted-foreground">데이터 없음</div>;
  }
  return (
    <div className="space-y-2">
      {rows.map((it) => {
        const pct = grand > 0 ? (it.total / grand) * 100 : 0;
        return (
          <div key={it.key} className="text-sm">
            <div className="mb-0.5 flex items-baseline justify-between gap-2">
              <span
                className={it.muted ? "text-muted-foreground" : "font-medium"}
                title={it.title}
              >
                {it.label}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {fmt(it.total)}건 · {pct.toFixed(1)}% · 평균 {fmtMs(it.avg_ms)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={
                  "h-full rounded-full " +
                  (it.muted ? "bg-muted-foreground/40" : "bg-primary")
                }
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NegativeList({ items }: { items: TopNegativeItem[] }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">데이터 없음</div>
    );
  }
  return (
    <ol className="space-y-2 text-sm">
      {items.map((it, i) => (
        <li key={it.query_id} className="flex items-start gap-2">
          <span className="w-5 shrink-0 text-right text-muted-foreground">
            {i + 1}.
          </span>
          <span className="flex-1">{it.question}</span>
          <span className="font-semibold text-red-600">{it.count}</span>
        </li>
      ))}
    </ol>
  );
}
